import {
  AmbientLight,
  DirectionalLight,
  MathUtils,
  OrthographicCamera,
  Plane,
  Raycaster,
  Scene,
  Vector2,
  Vector3,
  WebGLRenderer,
} from "three";
import type { Vec2, WorldBounds } from "../airport/AirportDefinition";
import { CAMERA } from "../config/gameplay";
import { LIGHTING, PALETTE } from "../config/palette";
import { planePoint } from "./geometry";

type CameraState = "home" | "crash" | "returning";

const HOME_TARGET = new Vector2();

/**
 * The breath's own factor for a traffic load of 0..1.
 *
 * Pure so the mapping can be pinned without a WebGL context.
 */
export function loadZoomFor(load: number): number {
  return MathUtils.lerp(1, CAMERA.loadZoom, MathUtils.clamp(load, 0, 1));
}

/**
 * The player's zoom with the breath folded in, floored at `zoomMin`.
 *
 * That floor is the whole safety story: `CAMERA.zoomMin` is chosen against
 * `GAMEPLAY.despawnMargin`, so a frame wider than it would show aircraft being
 * culled inside the view.
 */
export function breathedZoom(userZoom: number, loadZoom: number): number {
  return Math.max(CAMERA.zoomMin, userZoom * loadZoom);
}

/** Eased 0..1 ramp used by the crash zoom and the return home. */
export function smootherStep(value: number): number {
  const t = MathUtils.clamp(value, 0, 1);
  return t * t * (3 - 2 * t);
}

/**
 * One zoom step, clamped.
 *
 * Multiplicative rather than additive: a fixed increment is a big jump when
 * zoomed out and a barely visible one when zoomed in, so equal steps have to
 * be equal ratios.
 */
export function stepZoom(
  current: number,
  factor: number,
  min: number,
  max: number,
): number {
  return MathUtils.clamp(current * factor, min, max);
}

/**
 * Isometric view.
 *
 * The camera is orthographic and sits on a fixed offset that is orbited around
 * Z, so the world always reads as the same axonometric projection. The canvas
 * is rendered at a fraction of its CSS size and stretched back up — that
 * downscale is what gives the world its chunky pixel edges.
 */
export class AirportRenderer {
  readonly scene = new Scene();
  /**
   * The near plane sits behind the camera on purpose.
   *
   * With a positive near, a tall viewport pushes the bottom of the frustum
   * below the ground plane and clips the last few percent of the screen — the
   * ground quad simply is not drawn there. It is invisible today only because
   * the clear colour matches the ground, and it also sends every pointer event
   * in that band down `screenToWorld`'s fallback. An orthographic projection
   * has no perspective divide, so a negative near is free.
   */
  readonly camera = new OrthographicCamera(-50, 50, 50, -50, -1000, 1000);
  private readonly renderer: WebGLRenderer;
  private readonly ambient = new AmbientLight(
    LIGHTING.ambient.color,
    LIGHTING.ambient.intensity,
  );
  private readonly sun = new DirectionalLight(
    LIGHTING.sun.color,
    LIGHTING.sun.intensity,
  );

  private readonly raycaster = new Raycaster();
  private readonly pointer = new Vector2();
  private readonly groundPlane = new Plane(new Vector3(0, 0, 1), 0);
  private readonly hit = new Vector3();
  private readonly projected = new Vector3();

  private cssWidth = 1;
  private cssHeight = 1;
  unitsPerPixel = 1;
  viewBounds: WorldBounds = {
    minX: -80,
    maxX: 80,
    minY: -60,
    maxY: 60,
    corners: [
      { x: -80, y: -60 },
      { x: 80, y: -60 },
      { x: 80, y: 60 },
      { x: -80, y: 60 },
    ],
  };

  private readonly reducedMotion =
    typeof window !== "undefined" && typeof window.matchMedia === "function"
      ? window.matchMedia("(prefers-reduced-motion: reduce)").matches
      : false;

  private readonly cameraTarget = new Vector2();
  private readonly cameraFrom = new Vector2();
  private crashTarget: Vector2 | null = null;
  private cameraZoomFrom = 1;
  /**
   * The player's zoom, kept apart from `camera.zoom`.
   *
   * `camera.zoom` is what the crash and return-home transitions animate; this
   * is the level they animate back to, so a run that ends while zoomed in
   * comes home zoomed in.
   */
  private userZoom = 1;
  private userZoomTarget = 1;
  /**
   * The overload breath: a second, automatic lens on top of the player's.
   *
   * Kept separate from `userZoom` so the rail buttons still report the level
   * the player chose, and folded in only by `homeZoom()`.
   */
  private loadZoom = 1;
  private loadZoomTarget = 1;
  private cameraOrbit = 0;
  private cameraOrbitTarget = 0;
  private cameraOrbitVelocity = 0;
  private cameraOrbitFrom = 0;
  private cameraTransitionStartedAt = 0;
  private cameraUpdatedAt = 0;
  private cameraState: CameraState = "home";

  /**
   * Three rebuilds its background module when the GPU restores a lost context,
   * and that module defaults to a black clear colour. Registered after the
   * renderer so it runs after three's own restore handler.
   */
  private readonly handleContextRestored = (): void => {
    this.renderer.setClearColor(PALETTE.background, 1);
    this.onContextRestored?.();
  };

  private readonly handleContextLost = (): void => {
    this.onContextLost?.();
  };

  /**
   * Told when the GPU drops and regains the context.
   *
   * Three stops rendering by itself, but the simulation does not — without
   * these the game keeps advancing behind a frozen picture and can end a run
   * the player never saw.
   */
  onContextLost: (() => void) | null = null;
  onContextRestored: (() => void) | null = null;

  constructor(canvas: HTMLCanvasElement) {
    this.renderer = new WebGLRenderer({
      canvas,
      antialias: false,
      powerPreference: "high-performance",
    });
    this.renderer.setClearColor(PALETTE.background, 1);
    canvas.addEventListener("webglcontextlost", this.handleContextLost);
    canvas.addEventListener("webglcontextrestored", this.handleContextRestored);
    this.sun.position.set(
      LIGHTING.sun.position.x,
      LIGHTING.sun.position.y,
      LIGHTING.sun.position.z,
    );
    this.scene.add(this.ambient, this.sun);
    this.camera.up.set(0, 0, 1);
    this.cameraUpdatedAt = now();
    this.applyCamera();
  }

  /** Zoom in on a crash site; `immediate` skips the transition. */
  focusCrash(position: Vec2, immediate = false): void {
    if (this.reducedMotion) {
      return;
    }
    this.updateCamera(now());
    this.crashTarget = new Vector2(position.x, position.y);
    this.cameraOrbitFrom = this.cameraOrbit;
    this.cameraOrbitVelocity = 0;
    this.cameraState = "crash";

    if (immediate) {
      this.cameraFrom.copy(this.crashTarget);
      this.cameraTarget.copy(this.crashTarget);
      this.cameraZoomFrom = this.crashZoom();
      this.camera.zoom = this.crashZoom();
      this.cameraTransitionStartedAt = now() - CAMERA.crashHoldMs;
      this.applyCamera();
      this.camera.updateProjectionMatrix();
      return;
    }

    this.cameraFrom.copy(this.cameraTarget);
    this.cameraZoomFrom = this.camera.zoom;
    this.cameraTransitionStartedAt = now();
  }

  /**
   * How much airborne traffic there is, 0..1 — the view eases out with it.
   *
   * Called from the HUD tick, not per frame; the ease is what makes it read as
   * a swell rather than a stutter. Ignored under reduced motion: a camera that
   * drifts on its own is exactly what that setting is about.
   */
  setTrafficLoad(load: number): void {
    if (this.reducedMotion) {
      return;
    }
    this.loadZoomTarget = loadZoomFor(load);
  }

  /**
   * The player's zoom with the breath folded in.
   *
   * Floored at `zoomMin` on purpose: that limit is tied to
   * `GAMEPLAY.despawnMargin`, and a breath that pushed past it would show
   * aircraft being culled inside the frame.
   */
  private homeZoom(): number {
    return breathedZoom(this.userZoom, this.loadZoom);
  }

  /** Never zooms out for a crash: the player's level is the floor. */
  private crashZoom(): number {
    return Math.max(this.homeZoom(), CAMERA.crashZoom);
  }

  /**
   * One step in or out. Ignored away from home, the same way `rotateBy` is —
   * the crash zoom owns the camera until the run is reset.
   */
  zoomBy(factor: number): void {
    if (this.cameraState !== "home") {
      return;
    }
    this.updateCamera(now());
    this.userZoomTarget = stepZoom(
      this.userZoomTarget,
      factor,
      CAMERA.zoomMin,
      CAMERA.zoomMax,
    );
    if (this.reducedMotion) {
      this.userZoom = this.userZoomTarget;
      this.camera.zoom = this.homeZoom();
      this.camera.updateProjectionMatrix();
    }
  }

  /** The level the zoom is heading for, for the HUD's button states. */
  zoomLevel(): number {
    return this.userZoomTarget;
  }

  resetFocus(): void {
    this.cameraState = "home";
    this.crashTarget = null;
    this.cameraTarget.set(0, 0);
    this.userZoom = this.userZoomTarget;
    this.loadZoom = this.loadZoomTarget;
    this.camera.zoom = this.homeZoom();
    this.cameraOrbit = this.cameraOrbitTarget;
    this.cameraOrbitVelocity = 0;
    this.cameraUpdatedAt = now();
    this.applyCamera();
    this.camera.updateProjectionMatrix();
  }

  /** Returns the animation duration in ms. */
  returnHome(): number {
    if (this.reducedMotion) {
      this.resetFocus();
      return 0;
    }
    const timestamp = now();
    this.updateCamera(timestamp);
    this.cameraFrom.copy(this.cameraTarget);
    this.cameraZoomFrom = this.camera.zoom;
    this.cameraOrbitFrom = this.cameraOrbit;
    this.cameraState = "returning";
    this.cameraTransitionStartedAt = timestamp;
    return CAMERA.returnHomeMs;
  }

  rotateBy(degrees: number): void {
    if (this.cameraState !== "home") {
      return;
    }
    this.updateCamera(now());
    this.cameraOrbitTarget += MathUtils.degToRad(degrees);
    if (this.reducedMotion) {
      this.cameraOrbit = this.cameraOrbitTarget;
      this.cameraOrbitVelocity = 0;
      this.applyCamera();
      this.camera.updateProjectionMatrix();
    }
  }

  rotationDegrees(): number {
    return MathUtils.radToDeg(this.cameraOrbit);
  }

  updateCamera(timestamp: number): void {
    const delta = Math.min(
      0.25,
      Math.max(0, (timestamp - this.cameraUpdatedAt) / 1000),
    );
    this.cameraUpdatedAt = timestamp;

    if (this.cameraState === "home") {
      const zoomSettled = this.easeZoom(delta);
      const offset = this.cameraOrbit - this.cameraOrbitTarget;
      if (
        zoomSettled &&
        Math.abs(offset) < 1e-5 &&
        Math.abs(this.cameraOrbitVelocity) < 1e-4
      ) {
        this.cameraOrbit = this.cameraOrbitTarget;
        this.cameraOrbitVelocity = 0;
        return;
      }
      // Critically damped spring towards the target angle.
      const omega = 4 / CAMERA.orbitSmoothing;
      const decay = Math.exp(-omega * delta);
      const temp = this.cameraOrbitVelocity + omega * offset;
      this.cameraOrbit =
        this.cameraOrbitTarget + (offset + temp * delta) * decay;
      this.cameraOrbitVelocity =
        (this.cameraOrbitVelocity - omega * temp * delta) * decay;
      this.applyCamera();
      this.camera.updateProjectionMatrix();
      return;
    }

    const sinceStart = timestamp - this.cameraTransitionStartedAt;

    if (this.cameraState === "crash" && this.crashTarget) {
      const t = smootherStep(sinceStart / CAMERA.crashZoomMs);
      this.cameraTarget.lerpVectors(this.cameraFrom, this.crashTarget, t);
      this.camera.zoom = MathUtils.lerp(
        this.cameraZoomFrom,
        this.crashZoom(),
        t,
      );
      const drifting = Math.max(0, sinceStart - CAMERA.crashHoldMs) / 1000;
      const eased =
        drifting < CAMERA.crashDriftDelay
          ? (drifting * drifting) / (2 * CAMERA.crashDriftDelay)
          : drifting - CAMERA.crashDriftDelay / 2;
      this.cameraOrbit = this.cameraOrbitFrom + eased * CAMERA.crashDriftRate;
    } else if (this.cameraState === "returning") {
      const t = smootherStep(sinceStart / CAMERA.returnHomeMs);
      this.cameraTarget.lerpVectors(this.cameraFrom, HOME_TARGET, t);
      this.camera.zoom = MathUtils.lerp(
        this.cameraZoomFrom,
        this.homeZoom(),
        t,
      );
      this.cameraOrbit = MathUtils.lerp(
        this.cameraOrbitFrom,
        this.cameraOrbitTarget,
        t,
      );
      if (t >= 1) {
        this.cameraState = "home";
        this.crashTarget = null;
        this.cameraTarget.set(0, 0);
        this.camera.zoom = this.homeZoom();
        this.cameraOrbit = this.cameraOrbitTarget;
        this.cameraOrbitVelocity = 0;
      }
    }

    this.applyCamera();
    this.camera.updateProjectionMatrix();
  }

  /**
   * Eases the zoom towards the player's target. Returns true once there is
   * nothing left to move, which is what lets the home branch idle.
   */
  private easeZoom(delta: number): boolean {
    let settled = true;

    if (this.userZoom !== this.userZoomTarget) {
      const offset = this.userZoomTarget - this.userZoom;
      this.userZoom =
        Math.abs(offset) < 1e-3
          ? this.userZoomTarget
          : this.userZoom +
            offset * (1 - Math.exp(-delta / CAMERA.zoomSmoothing));
      settled = false;
    }

    // The breath eases on its own, far slower, and on a tighter tolerance: a
    // 4.5% move settled at 1e-3 would visibly step on the last frame.
    if (this.loadZoom !== this.loadZoomTarget) {
      const offset = this.loadZoomTarget - this.loadZoom;
      this.loadZoom =
        Math.abs(offset) < 1e-5
          ? this.loadZoomTarget
          : this.loadZoom +
            offset * (1 - Math.exp(-delta / CAMERA.loadZoomSmoothing));
      settled = false;
    }

    this.camera.zoom = this.homeZoom();
    return settled;
  }

  private applyCamera(): void {
    const cos = Math.cos(this.cameraOrbit);
    const sin = Math.sin(this.cameraOrbit);
    const { x, y, z } = CAMERA.offset;
    this.camera.position.set(
      x * cos - y * sin + this.cameraTarget.x,
      x * sin + y * cos + this.cameraTarget.y,
      z,
    );
    this.camera.lookAt(this.cameraTarget.x, this.cameraTarget.y, 0);
  }

  resize(cssWidth: number, cssHeight: number): WorldBounds {
    this.cssWidth = Math.max(1, cssWidth);
    this.cssHeight = Math.max(1, cssHeight);

    const pixelScale =
      this.cssWidth >= CAMERA.desktopMinWidth
        ? CAMERA.desktopPixelScale
        : CAMERA.mobilePixelScale;
    const drawWidth = Math.max(1, Math.floor(this.cssWidth / pixelScale));
    const drawHeight = Math.max(1, Math.floor(this.cssHeight / pixelScale));
    this.renderer.setPixelRatio(1);
    this.renderer.setSize(drawWidth, drawHeight, false);
    this.renderer.domElement.style.width = "100%";
    this.renderer.domElement.style.height = "100%";

    const shortUnits =
      this.cssWidth >= CAMERA.desktopMinWidth
        ? CAMERA.desktopShortUnits
        : CAMERA.mobileShortUnits;
    this.unitsPerPixel = shortUnits / Math.min(this.cssWidth, this.cssHeight);

    const halfWidth = (this.cssWidth / 2) * this.unitsPerPixel;
    const halfHeight = (this.cssHeight / 2) * this.unitsPerPixel;
    this.camera.left = -halfWidth;
    this.camera.right = halfWidth;
    this.camera.top = halfHeight;
    this.camera.bottom = -halfHeight;
    this.camera.updateProjectionMatrix();

    const corners = this.homeCorners();
    this.viewBounds = {
      minX: Math.min(...corners.map((corner) => corner.x)),
      maxX: Math.max(...corners.map((corner) => corner.x)),
      minY: Math.min(...corners.map((corner) => corner.y)),
      maxY: Math.max(...corners.map((corner) => corner.y)),
      corners,
    };
    return this.viewBounds;
  }

  /**
   * The view quad at zoom 1, whatever the camera is doing right now.
   *
   * These corners are the airspace: `GameSession` spawns arrivals along them
   * and culls past them. Measuring them through the player's zoom would let a
   * zoom-in strand traffic outside a shrunken quad and a zoom-out move where
   * arrivals are born, mid-run — the zoom is a lens, not a rule change.
   */
  private homeCorners(): Vec2[] {
    const zoom = this.camera.zoom;
    this.camera.zoom = 1;
    this.camera.updateProjectionMatrix();
    const corners = [
      this.screenToWorld(0, 0),
      this.screenToWorld(this.cssWidth, 0),
      this.screenToWorld(this.cssWidth, this.cssHeight),
      this.screenToWorld(0, this.cssHeight),
    ];
    this.camera.zoom = zoom;
    this.camera.updateProjectionMatrix();
    return corners;
  }

  screenToWorld(x: number, y: number): Vec2 {
    this.pointer.set(
      (x / this.cssWidth) * 2 - 1,
      -(y / this.cssHeight) * 2 + 1,
    );
    this.raycaster.setFromCamera(this.pointer, this.camera);

    // `planePoint` rather than `Ray.intersectPlane`: on a tall viewport the
    // orthographic near plane dips below the ground — at 390x844 the bottom
    // corners start at z = -11.6 — and a bounded intersection would send them
    // to the flat fallback below, which does not orbit with the camera.
    // `resize` feeds those corners to `isOutsideBounds`, whose circumscribed
    // circle then drifts off the airport and culls aircraft in mid-view.
    if (planePoint(this.raycaster.ray, this.groundPlane, this.hit)) {
      return { x: this.hit.x, y: this.hit.y };
    }

    // Only reachable if the camera ever looks along the ground plane.
    return {
      x: (x - this.cssWidth / 2) * this.unitsPerPixel,
      y: (this.cssHeight / 2 - y) * this.unitsPerPixel,
    };
  }

  worldToScreen(point: Vec2): { x: number; y: number } {
    this.projected.set(point.x, point.y, 0).project(this.camera);
    return {
      x: ((this.projected.x + 1) / 2) * this.cssWidth,
      y: ((1 - this.projected.y) / 2) * this.cssHeight,
    };
  }

  /** World units covered by a screen-space distance, for touch pick radii. */
  worldUnitsForPixels(pixels: number): number {
    const centre = this.screenToWorld(this.cssWidth / 2, this.cssHeight / 2);
    const right = this.screenToWorld(
      this.cssWidth / 2 + pixels,
      this.cssHeight / 2,
    );
    const down = this.screenToWorld(
      this.cssWidth / 2,
      this.cssHeight / 2 + pixels,
    );
    return Math.max(
      Math.hypot(right.x - centre.x, right.y - centre.y),
      Math.hypot(down.x - centre.x, down.y - centre.y),
    );
  }

  render(): void {
    this.updateCamera(now());
    this.renderer.render(this.scene, this.camera);
  }

  dispose(): void {
    this.renderer.domElement.removeEventListener(
      "webglcontextlost",
      this.handleContextLost,
    );
    this.renderer.domElement.removeEventListener(
      "webglcontextrestored",
      this.handleContextRestored,
    );
    this.renderer.dispose();
  }
}

function now(): number {
  return typeof performance !== "undefined" ? performance.now() : Date.now();
}
